import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import clsx from 'clsx'
import useMutation from 'hooks/useMutation'
import { Building } from 'models/Building'
import { useRecoilState, useRecoilValue } from 'recoil'
import { listPointState } from 'store/buildingStore'
import currentViewerState from 'store/viewerStore'
import useSWR from 'swr'
import { BuildingFile, Point } from 'types/building'
import Krpano from 'utils/krpano'

import LazyThumbnail, { ImageType } from 'components/atoms/LazyThumbnail'
import RotatedArrowIcon from 'components/icons/RotatedArrowIcon'

type Props = {
  point: Point
}

export default function ViewerPointImages({ point }: Props) {
  const { id: buildingId } = useParams()
  const [searchParams] = useSearchParams()
  const isAnnotationDisplay = searchParams.get('is_display')
  const { id, image360, order } = point
  const [isExpanded, setIsExpanded] = useState(false)
  const isFirstLoading = useRef<boolean>(true)

  const points = useRecoilValue(listPointState)
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)

  const { data: building, mutate: refetchBuilding } = useSWR<Building>(
    {
      url: `/v1/tour/buildings/${buildingId}`
    },
    {
      revalidateOnMount: false
    }
  )
  const listSubImages = useMemo(
    () =>
      currentViewer.pointDetail?.id === point.id
        ? [...(currentViewer.pointDetail?.subImages || [])].sort(
            (a, b) => Number(a.order) - Number(b.order)
          )
        : [],
    [
      point.id,
      currentViewer.pointDetail?.id,
      currentViewer.pointDetail?.subImages
    ]
  )

  const directionList = useMemo(
    () =>
      currentViewer.pointDetail?.directions
        ?.map((item) => {
          const order = points.find(
            (p) =>
              p.id !== currentViewer.selectedPoint && item.id.includes(p.id)
          )?.order
          return !order ? { ...item, target: null } : { ...item, target: order }
        })
        .filter((item: any) => item.target),
    [currentViewer.pointDetail?.directions, currentViewer.selectedPoint, points]
  )

  const handleSelectPoint = (
    selectedImageFile?: BuildingFile,
    pointId?: string
  ) => {
    if (pointId && currentViewer.selectedPoint !== pointId) {
      setIsExpanded(false)
      setCurrentViewer((prev) => ({
        ...prev,
        selectedPoint: pointId,
        selectedImage: selectedImageFile
          ? selectedImageFile.id
          : point.image360?.id,
        isClickToDirection: false,
        isSelectPointInLeftMenu: true
      }))
      return
    }
    if (selectedImageFile) {
      setCurrentViewer((prev) => ({
        ...prev,
        selectedImage: selectedImageFile.id
      }))
    } else {
      setCurrentViewer((prev) => ({
        ...prev,
        selectedImage: currentViewer.pointDetail?.image360?.id
      }))
    }
  }

  const { trigger: getPointDetail } = useMutation(
    `v1/tour/points/${currentViewer.selectedPoint}?building_id=${buildingId}&floor_id=${currentViewer.selectedFloor}`
  )
  useEffect(() => {
    const handlePointChange = async () => {
      if (
        currentViewer.isClickToDirection &&
        !currentViewer.isSelectPointInLeftMenu
      ) {
        setIsExpanded(false)
        return
      }

      //   if change point by click into menu left
      const panoViewer = document.getElementById('embedpano-viewer') as any
      if (!panoViewer) return

      const currentSelectedPoint = points.find(
        (p) => p.id === currentViewer.selectedPoint
      )
      if (id !== currentViewer.selectedPoint) return
      if (
        !panoViewer ||
        !currentSelectedPoint ||
        !currentSelectedPoint.initView
      )
        return
      const file =
        currentSelectedPoint.subImages?.find(
          (img) => img.id === currentViewer.selectedImage
        ) || currentSelectedPoint.image360
      if (!file) return
      const imageFile = { ...file }
      let annotations = currentSelectedPoint?.annotations || []
      const mainImage =
        currentViewer.selectedImage === currentSelectedPoint?.image360?.id

      const data = await getPointDetail({
        method: 'get',
        data: null
      })

      const dataCurrentFile =
        data?.subImages?.find(
          (item: any) => item.id === currentViewer.selectedImage
        ) || data?.image360
      if (dataCurrentFile) {
        imageFile.tilesUrl = dataCurrentFile.tilesUrl
        imageFile.multires = dataCurrentFile.multires
        imageFile.s3Path = dataCurrentFile.s3Path || ''
        imageFile.thumbnail = dataCurrentFile.thumbnailUrl || ''
        annotations = data?.annotations?.filter((item: any) => {
          // An annotation should be displayed if:
          // 1. It has no specific imageID (belongs to the whole point).
          // 2. Its imageID matches the image's ID .
          return (
            (currentSelectedPoint?.image360?.id ===
              currentViewer.selectedImage &&
              !item.imageID) ||
            item.imageID === currentViewer.selectedImage
          )
        })
      }

      // if change point with direction click return
      const h = mainImage
        ? currentSelectedPoint.initView.h
        : dataCurrentFile?.initView?.h
      const v = mainImage
        ? currentSelectedPoint.initView.v
        : dataCurrentFile?.initView?.v
      const z = mainImage
        ? currentSelectedPoint.initView.z
        : dataCurrentFile?.initView?.z

      const tilesUrl = imageFile.tilesUrl || ''
      const previewUrl = imageFile.thumbnail

      const { isLocked } = currentSelectedPoint
      const { multires } = imageFile
      const imageUrl = imageFile.s3Path
      const urlH = searchParams.get('hlookat')
      const urlV = searchParams.get('vlookat')
      const urlZ = searchParams.get('fov')

      // Always re-add all annotations, directions, stairways after loading any image
      // const annotations = currentSelectedPoint?.annotations || []
      const directions = directionList
      const stairways = data?.stairways?.map((item: any) => ({
        ...item,
        nextFloorName: building?.floors.find(
          (floor) => floor.id === +item.nextFloorId
        )?.name
      }))
      const sceneName = Krpano.getScenceNameByFileId(
        currentViewer.selectedPoint,
        points,
        currentViewer.selectedImage
      )

      if (panoViewer.get(`scene[${sceneName}]`) === null) {
        const screenString = Krpano.generateSceneXML({
          sceneName,
          tilesUrl: tilesUrl || undefined,
          multires: multires || undefined,
          s3Path: imageUrl,
          thumbnailUrl: previewUrl || ''
        })
        panoViewer.call(`loadxml(${screenString}, null, MERGE);`)
      }
      const currentH = urlH && isFirstLoading.current ? urlH : h
      const currentV = urlV && isFirstLoading.current ? urlV : v
      const currentZ = urlZ && isFirstLoading.current ? urlZ : z

      panoViewer.call(
        `load_scene(${sceneName},${currentH},${currentV},${currentZ})`
      )
      isFirstLoading.current = false

      annotations &&
        Krpano.AddAnnotation(panoViewer, annotations, isAnnotationDisplay)
      directions && Krpano.AddDirectionArrow(panoViewer, data.directions)
      stairways && Krpano.AddStairway(panoViewer, stairways)
      Krpano.TogglePointLock(isLocked)
    }
    // avoid useEffect multiple times in other point
    if (id !== currentViewer.selectedPoint) {
      return
    }
    handlePointChange()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewer.selectedImage])

  const handleToggle = () => {
    if (
      !currentViewer.selectedPoint ||
      currentViewer.selectedPoint !== point.id
    ) {
      setCurrentViewer((prev) => ({ ...prev, selectedPoint: point.id }))
    }
    setIsExpanded((prev) => !prev)
  }

  useEffect(() => {
    if (isExpanded) {
      setCurrentViewer((prev) => ({
        ...prev,
        isSelectPointInLeftMenu: true,
        isClickToDirection: false
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded])

  useEffect(() => {
    if (id !== currentViewer.selectedPoint) return
    refetchBuilding()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewer.selectedFloor, refetchBuilding])

  return (
    <>
      <div
        className={clsx('p-[10px] min-h-[36px]', {
          'theme-bg-sub-primary':
            currentViewer.selectedPoint === id &&
            currentViewer.selectedImage === image360?.id
        })}
        onClick={() => handleSelectPoint(point.image360 || undefined, point.id)}
      >
        <div
          className={`absolute ${
            image360 ? 'top-[15px] left-[16px]' : 'top-[10px] left-[10px]'
          } z-10 rounded-lg flex bg-[#021120] p-[2px] min-w-[16px]`}
        >
          <div className="m-auto text-[13px] leading-[13px] font-bold text-white">
            {order}
          </div>
        </div>

        {point.isSubImages && (
          <div
            className="absolute top-[15px] left-[35px] z-10 rounded-full flex bg-[#787B82CC] w-[18px] h-[18px]"
            onClick={handleToggle}
          >
            <RotatedArrowIcon className="m-auto" />
          </div>
        )}

        {image360 && (
          <LazyThumbnail
            imageType={ImageType.OBJECT_FILL}
            url={image360.thumbnail || image360.s3Path}
            name={image360.name}
          />
        )}
      </div>

      {isExpanded &&
        listSubImages.map((item) => (
          <div
            key={item.id}
            className={clsx('p-[10px] min-h-[36px]', {
              'theme-bg-sub-primary': currentViewer.selectedImage === item.id
            })}
            onClick={() => handleSelectPoint(item, point.id)}
          >
            <LazyThumbnail
              imageType={ImageType.OBJECT_FILL}
              url={item.thumbnail}
              name={item.name}
            />
          </div>
        ))}
    </>
  )
}
